/* eslint-disable i18next/no-literal-string */
import { cn } from "~/lib/utils";
import { colors } from "~/components/landing/colors";

// Served from public/ (not imported) so it stays a plain file URL. As a bundled
// asset it's under Vite's 4 KB inline limit and becomes a data: URI, whose
// unescaped spaces/quotes break `mask-image: url(...)` — dropping the mask and
// leaking the raw gradient. The original loaded it as a file URL too.
const PLUS_ICON_URL = "/landing/plus-circle-icon.svg";

/**
 * Ported from bouncieweb's `LandingPageComponents/BentoToggleIcon.js` +
 * `ToggleIconColors.js`.
 *
 * A plus-in-a-circle toggle pinned to the top-right of a bento item. The plus
 * shape is a mask over an animated conic-gradient (a slowly orbiting rainbow
 * blur). When opened, the whole icon rotates 45deg (plus -> x) and a solid
 * `blueLight9` layer fades in over the gradient.
 *
 * Original combined the two components; `BentoToggleIcon` rotated the outer
 * 30px box and `ToggleIconColors` also rotated its own wrapper — a double
 * rotation. We reproduce that by rotating the button (45deg) and the inner
 * wrapper (another 45deg) on open, matching the original visual.
 *
 * The three orbiting keyframes are scoped in a uniquely-named <style>.
 */
export default function BentoToggleIcon({
  className,
  clickHandler,
  toggleOn,
  sid,
}: {
  /** Which bento card this toggles; becomes part of the data-sid. */
  sid: string;
  className?: string;
  clickHandler: () => void;
  toggleOn: boolean;
}) {
  return (
    <button data-sid={`bento-toggle:${sid}`}
      type="button"
      onClick={clickHandler}
      className={cn(
        "absolute top-0 right-0 cursor-pointer border-none bg-transparent p-4",
        className,
      )}
    >
      <style>{`
        @keyframes cf-toggle-rotate {
          0%   { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
        @keyframes cf-toggle-left {
          0%   { left: -110%; }
          50%  { left: -40%; }
          100% { left: -110%; }
        }
        @keyframes cf-toggle-top {
          0%   { top: -40%; }
          50%  { top: -110%; }
          100% { top: -40%; }
        }
      `}</style>
      {/* ToggleIconColors Wrapper — 30px box, rotates 45deg on open */}
      <div
        className="relative h-[30px] w-[30px] transition-transform duration-300 ease-out"
        style={{ transform: toggleOn ? "rotate(45deg)" : "rotate(0deg)" }}
      >
        {/* IconMask — plus-circle svg as a mask over the gradient/bg layers */}
        <div
          className="absolute h-full w-full"
          style={{
            maskImage: `url(${PLUS_ICON_URL})`,
            WebkitMaskImage: `url(${PLUS_ICON_URL})`,
            maskRepeat: "no-repeat",
            WebkitMaskRepeat: "no-repeat",
            maskSize: "contain",
            WebkitMaskSize: "contain",
          }}
        >
          {/* Gradient — orbiting rainbow conic gradient */}
          <div
            className="absolute"
            style={{
              top: "-75%",
              left: "-75%",
              width: "250%",
              height: "250%",
              background:
                "conic-gradient(aqua, green, cornflowerblue, aqua)",
              filter: "blur(3px)",
              animationName:
                "cf-toggle-left, cf-toggle-top, cf-toggle-rotate",
              animationDuration: "6.5s, 6s, 5.5s",
              animationTimingFunction: "ease, ease, linear",
              animationIterationCount: "infinite",
            }}
          />
          {/* BgColor — solid layer fades in when open */}
          <div
            className={cn(
              "absolute top-0 left-0 h-full w-full opacity-0 transition-opacity duration-300 ease-out",
              toggleOn && "opacity-100",
            )}
            style={{ background: colors.blueLight9 }}
          />
        </div>
      </div>
    </button>
  );
}
